import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSession } from "@clerk/clerk-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CircleNotch as Loader2, Key, Check, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { toastError } from "@/lib/userMessages";
import { adminConfigApi, type PlatformConfigEntry } from "../api/adminConfig";
import { ACTIVE_OPENAI_MODELS, OPENAI_ONLY_MODE } from "@/lib/ai/providerPolicy";

interface EditRowProps {
  entry: PlatformConfigEntry;
  onSaved: () => void;
}

type PlaintextControl = "boolean" | "model" | "rate-limit" | "text";

const SETTING_DETAILS: Record<
  string,
  { control: PlaintextControl; description: string; label?: string }
> = {
  beta_auto_approve: {
    control: "boolean",
    description: "Aprova automaticamente inscrições beta após o cadastro.",
  },
  beta_form_enabled: {
    control: "boolean",
    description: "Exibe o formulário de inscrição beta para novos usuários.",
  },
  default_model: {
    control: "model",
    description:
      "Modelo padrão para integrações sem modelo próprio. Agentes e fluxos já configurados mantêm seu modelo atual.",
  },
  rate_limit_rph: {
    control: "rate-limit",
    description:
      "Valor administrativo de referência em requisições por hora. Os limites efetivos continuam definidos pelos provedores e fluxos que os aplicam.",
    label: "Limite de requisições por hora",
  },
  openai_api_key: {
    control: "text",
    description: "Única integração de IA habilitada nesta instalação.",
    label: "Chave da OpenAI",
  },
};

function controlFor(entry: PlatformConfigEntry): PlaintextControl {
  return SETTING_DETAILS[entry.key]?.control ?? "text";
}

function initialValue(entry: PlatformConfigEntry): string {
  if (entry.plaintext_val) return entry.plaintext_val;
  return controlFor(entry) === "boolean" ? "false" : "";
}

function EditRow({ entry, onSaved }: EditRowProps) {
  const { session } = useSession();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const control = controlFor(entry);
  const details = SETTING_DETAILS[entry.key];
  const shownLabel = details?.label ?? entry.label;

  async function handleSave() {
    if (!value.trim()) { toast.error("Valor não pode ser vazio"); return; }
    const token = await session?.getToken();
    if (!token) return;
    setSaving(true);
    try {
      await adminConfigApi.set(entry.key, value, token);
      toast.success(`${entry.label} atualizado`);
      setValue("");
      setEditing(false);
      onSaved();
    } catch (e) {
      toastError(e, "Não foi possível salvar. Tente novamente.");
    } finally {
      setSaving(false);
    }
  }

  function handleCardClick() {
    if (!editing) {
      setValue(initialValue(entry));
      setEditing(true);
    }
  }

  function handleCancel(e: React.MouseEvent) {
    e.stopPropagation();
    setEditing(false);
    setValue("");
  }

  return (
    <div
      className={`p-4 border rounded-xl bg-card space-y-3 transition-all ${
        editing
          ? "border-primary/40"
          : "border-border/50 cursor-pointer hover:border-primary/30 hover:bg-card/80"
      }`}
      onClick={handleCardClick}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <div className="p-2 rounded-lg bg-muted shrink-0">
            <Key className="w-4 h-4 text-muted-foreground" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-foreground">{shownLabel}</span>
              {entry.is_configured ? (
                entry.source === "env" ? (
                  <Badge variant="outline" className="text-xs border-warning/40 text-warning">
                    <Check className="w-3 h-3 mr-1" /> Via ambiente
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-xs border-green-500/40 text-green-500">
                    <Check className="w-3 h-3 mr-1" /> Configurado
                  </Badge>
                )
              ) : (
                <Badge variant="outline" className="text-xs border-muted-foreground/40 text-muted-foreground">
                  Não configurado
                </Badge>
              )}
            </div>
            {entry.is_configured && entry.value_type === "secret" && entry.masked && (
              <p className="text-xs text-muted-foreground font-mono mt-0.5">{entry.masked}</p>
            )}
            {entry.is_configured && entry.value_type === "plaintext" && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {control === "boolean"
                  ? (entry.plaintext_val === "true" ? "Ativado" : "Desativado")
                  : entry.plaintext_val}
              </p>
            )}
            {details?.description && (
              <p className="text-xs text-muted-foreground mt-1">{details.description}</p>
            )}
          </div>
        </div>
        {editing && (
          <button
            onClick={handleCancel}
            className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {editing && (
        <div
          className="flex items-center gap-2 pt-1"
          onClick={(e) => e.stopPropagation()}
        >
          {entry.value_type === "secret" ? (
            <Input
              type="password"
              placeholder="sk-..."
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="font-mono text-sm"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") void handleSave();
                if (e.key === "Escape") { setEditing(false); setValue(""); }
              }}
            />
          ) : control === "boolean" ? (
            <div className="flex h-10 flex-1 items-center gap-3 rounded-md border border-input bg-background px-3">
              <Switch
                checked={value === "true"}
                onCheckedChange={(checked) => setValue(String(checked))}
                aria-label={shownLabel}
              />
              <span className="text-sm text-muted-foreground">
                {value === "true" ? "Ativado" : "Desativado"}
              </span>
            </div>
          ) : control === "model" ? (
            <Select value={value || undefined} onValueChange={setValue}>
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Selecione um modelo GPT-5.6" />
              </SelectTrigger>
              <SelectContent>
                {ACTIVE_OPENAI_MODELS.map((model) => (
                  <SelectItem key={model.id} value={model.id}>
                    {model.label} — {model.profile}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : control === "rate-limit" ? (
            <Input
              type="number"
              min="1"
              max="1000000"
              step="1"
              inputMode="numeric"
              placeholder="Ex.: 1000"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") void handleSave();
                if (e.key === "Escape") { setEditing(false); setValue(""); }
              }}
            />
          ) : (
            <Input
              type="text"
              placeholder="Novo valor"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") void handleSave();
                if (e.key === "Escape") { setEditing(false); setValue(""); }
              }}
            />
          )}
          <Button size="sm" onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvar"}
          </Button>
        </div>
      )}
    </div>
  );
}

export function ApiKeysSection() {
  const { session } = useSession();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "config"],
    queryFn: async () => {
      const token = await session?.getToken();
      if (!token) throw new Error("no_token");
      return adminConfigApi.list(token);
    },
    enabled: Boolean(session),
  });

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ["admin", "config"] });
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return <p className="text-sm text-destructive">Erro ao carregar configurações.</p>;
  }

  const secrets = data?.filter((entry) =>
    entry.value_type === "secret" && (!OPENAI_ONLY_MODE || entry.key === "openai_api_key")
  ) ?? [];
  const settings = data?.filter((e) => e.value_type === "plaintext") ?? [];

  return (
    <div className="space-y-6">
      {secrets.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">OpenAI</h3>
          {secrets.map((entry) => (
            <EditRow key={entry.key} entry={entry} onSaved={refresh} />
          ))}
        </div>
      )}
      {settings.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Configurações Globais</h3>
          {settings.map((entry) => (
            <EditRow key={entry.key} entry={entry} onSaved={refresh} />
          ))}
        </div>
      )}
    </div>
  );
}
